import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Eligibility.css';

function Eligibility() {
  var navigate = useNavigate();

  return (
    <main className="elig-page">
      <Helmet>
        <title>Eligibility</title>
        <meta name="description" content="Find out if you're eligible for Global Entry and what you need to apply." />
      </Helmet>

      {/* Hero */}
      <section className="elig-hero">
        <div className="elig-hero__inner">
          <h1 className="elig-hero__heading">Are you eligible for Global Entry?</h1>
          <p className="elig-hero__subtitle">
            Global Entry is a U.S. Customs and Border Protection program that lets pre-approved travelers use expedited kiosks when entering the United States. It also includes TSA PreCheck.
          </p>
        </div>
      </section>

      {/* Who's eligible */}
      <section className="elig-section">
        <div className="elig-section__inner">
          <div className="elig-two-col">
            <div className="elig-col">
              <div className="elig-col__icon elig-col__icon--green">
                <svg viewBox="0 0 24 24" fill="none" stroke="#2E8540" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="elig-col__heading">Who is eligible</h2>
              <ul className="elig-list">
                <li>U.S. citizens, including dual citizens</li>
                <li>U.S. lawful permanent residents (green card holders)</li>
                <li>U.S. nationals</li>
                <li>Canadian citizens and permanent residents (through NEXUS)</li>
                <li>Citizens of select partner countries including Argentina, India, Colombia, the UK, Germany, Netherlands, Singapore, South Korea, Switzerland, Panama, and the Dominican Republic</li>
                <li>Children under 18 with parent or legal guardian consent</li>
              </ul>
            </div>
            <div className="elig-col">
              <div className="elig-col__icon elig-col__icon--red">
                <svg viewBox="0 0 24 24" fill="none" stroke="#D63E04" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>
              <h2 className="elig-col__heading">Who may be denied</h2>
              <ul className="elig-list elig-list--red">
                <li>Applicants with any criminal conviction or pending charges</li>
                <li>Prior customs or immigration violations in any country</li>
                <li>Previously denied entry to or removed from the United States</li>
                <li>Subjects of an ongoing law enforcement investigation</li>
                <li>Applicants who provide false or incomplete information</li>
                <li>Anyone who cannot satisfy CBP of their low-risk status</li>
              </ul>
              <p className="elig-note">CBP has sole discretion in approving or denying applications.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="elig-section elig-section--alt">
        <div className="elig-section__inner">
          <h2 className="elig-section__heading">What you get with Global Entry</h2>
          <div className="elig-benefits">
            <div className="elig-benefit">
              <div className="elig-benefit__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#0071BC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="elig-benefit__title">Expedited customs kiosks</h3>
              <p className="elig-benefit__text">Skip the standard CBP inspection line when returning to the U.S. from international travel.</p>
            </div>
            <div className="elig-benefit">
              <div className="elig-benefit__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#0071BC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3 className="elig-benefit__title">TSA PreCheck included</h3>
              <p className="elig-benefit__text">Your Known Traveler Number works for TSA PreCheck at no additional cost. Keep your shoes and laptop in your bag.</p>
            </div>
            <div className="elig-benefit">
              <div className="elig-benefit__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#0071BC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h3 className="elig-benefit__title">5-year membership</h3>
              <p className="elig-benefit__text">One-time $100 fee covers 5 years. Many premium credit cards reimburse this automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application process */}
      <section className="elig-section">
        <div className="elig-section__inner">
          <h2 className="elig-section__heading">The application process</h2>
          <div className="elig-process">
            <div className="elig-process__step">
              <div className="elig-process__number">1</div>
              <div>
                <h3 className="elig-process__title">Apply online</h3>
                <p className="elig-process__text">Create an account on the Trusted Traveler Programs (TTP) website and complete the application. You'll need 5 years of employment and travel history.</p>
              </div>
            </div>
            <div className="elig-process__step">
              <div className="elig-process__number">2</div>
              <div>
                <h3 className="elig-process__title">Pay the $100 fee</h3>
                <p className="elig-process__text">Non-refundable application fee that covers the 5-year membership. Check if your credit card offers reimbursement.</p>
              </div>
            </div>
            <div className="elig-process__step">
              <div className="elig-process__number">3</div>
              <div>
                <h3 className="elig-process__title">Background check</h3>
                <p className="elig-process__text">CBP conducts a thorough review of your criminal history, customs records, and law enforcement databases. This typically takes 2&ndash;4 weeks.</p>
              </div>
            </div>
            <div className="elig-process__step">
              <div className="elig-process__number">4</div>
              <div>
                <h3 className="elig-process__title">Schedule your interview</h3>
                <p className="elig-process__text">Once conditionally approved, book an in-person interview at an enrollment center. This is where most people get stuck &mdash; slots can take 1&ndash;6+ months to find.</p>
              </div>
            </div>
            <div className="elig-process__step">
              <div className="elig-process__number">5</div>
              <div>
                <h3 className="elig-process__title">Complete your interview</h3>
                <p className="elig-process__text">A brief 10&ndash;15 minute session. Bring your valid passport and one additional ID (driver's license). Fingerprints and photo are taken on-site.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interview docs */}
      <section className="elig-section elig-section--alt">
        <div className="elig-section__inner">
          <h2 className="elig-section__heading">What to bring to your interview</h2>
          <div className="elig-docs">
            <div className="elig-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16v16H4z" />
                <path d="M9 9h6" />
                <path d="M9 13h6" />
                <path d="M9 17h4" />
              </svg>
              <span>Valid passport</span>
            </div>
            <div className="elig-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <path d="M2 10h20" />
              </svg>
              <span>Driver's license or state ID</span>
            </div>
            <div className="elig-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span>Permanent resident card (if applicable)</span>
            </div>
            <div className="elig-doc">
              <svg viewBox="0 0 24 24" fill="none" stroke="#112E51" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <span>Any relevant immigration documents</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="elig-cta">
        <div className="elig-cta__inner">
          <h2 className="elig-cta__heading">The hardest part? Getting the interview.</h2>
          <p className="elig-cta__text">That's where we come in. Easy Global Entry monitors appointment availability 24/7 and books the moment a slot opens.</p>
          <button className="home-hero__cta" onClick={function() { navigate('/get-started'); window.scrollTo(0, 0); }}>
            Get Started &rarr;
          </button>
        </div>
      </section>
    </main>
  );
}

export default Eligibility;
